import { useDisclosure } from '@chakra-ui/react';
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { Endpoints } from '@octokit/types';
import { BoardIcon, CloseIcon, GithubIcon } from '@sketchnote/icons';
import { Emoji } from 'emoji-mart';
import React, { useEffect, useState } from 'react';
import { API } from 'src/api';

interface IntegrationType {
  createdAt: string;
  file: {
    file: any;
    filetype: string;
  };
  hook: Endpoints['GET /repos/{owner}/{repo}/hooks/{hook_id}']['response']['data'];
  hookId: number;
  member: string;
  repo: Endpoints['GET /repos/{owner}/{repo}']['response']['data'];
  repoId: number;
  repoName: string;
  updatedAt: string;
  _id: string;
}

export const GithubPage = ({ onClose }: { onClose: () => void }) => {
  const [integrations, setIntegrations] = useState<IntegrationType[]>([]);
  const [isLoading, setLoader] = useState<boolean>(true);
  const [hasGitAccess, setHasGitAccess] = useState<boolean>(false);
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();
  const [openSnackbar] = useSnackbar();

  async function fetchGithubIntegrations() {
    try {
      const gitCheck = await API.get('/integrations/?type=github&data=check');

      if (gitCheck?.data?.data?._id) {
        const response = await API.get('/integrations?type=github&data=settings');
        setHasGitAccess(true);
        setLoader(false);
        if (response.data.success) {
          return setIntegrations(response.data.data);
        }
      } else {
        setLoader(false);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  const filterRemovedIntegration = (id) => {
    setIntegrations((prevInt) => prevInt.filter((i) => i._id !== id));
  };

  const handleGitAuthRemoval = async () => {
    try {
      const response = await API.delete(`/integrations/auth?type=github`);

      onModalClose();
      if (response.data.success) {
        openSnackbar({
          text: 'The github integration was removed',
          snackType: 'standard',
          position: 'bottom-center',
        });
        setHasGitAccess(false);
        setIntegrations([]);
      } else {
        throw new Error();
      }
    } catch (error) {
      onModalClose();
      openSnackbar({
        text: 'There was an error in removing the integration',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  useEffect(() => {
    fetchGithubIntegrations();
  }, []);

  return (
    <div className="overflow-y-auto px-48" style={{ height: '59rem' }}>
      <section className="py-48 pb-28 flex justify-between items-center">
        <div className="flex">
          <GithubIcon wrapperClass="pr-16" size={46} />
          <div>
            <div className="mb-10">
              <div className="font-medium">Github</div>
              <div className="text-sm">Software Development</div>
            </div>
          </div>
        </div>
        {hasGitAccess && (
          <button
            className="border-red-700 text-red-700 border rounded-sm px-24 h-40"
            onClick={onOpen}
          >
            Disconnect
          </button>
        )}
      </section>
      <section>
        <div className="font-medium mb-10">Connected files ({integrations.length})</div>
        <div className="flex py-12 px-20">
          <span className="w-1/3">File name</span>
          <span className="w-1/2">Repo</span>
        </div>
        <div className="pb-48">
          {integrations.length === 0 && isLoading && (
            <span className="flex border rounded-md py-12 px-20 animate-pulse bg-gray-300">
              &nbsp;
            </span>
          )}
          {integrations.length === 0 && !isLoading && (
            <span className="flex border rounded-md py-12 px-20">No integrations found</span>
          )}
          {integrations.map((item) => (
            <IntegrationTile
              key={item._id}
              item={item}
              onClose={onClose}
              filterRemovedIntegration={filterRemovedIntegration}
            />
          ))}
        </div>
      </section>
      <Modal isOpen={isOpen} onClose={onModalClose}>
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">
            Are you sure you want to remove your Github integration with Sketchnote?
          </p>
          <div className="flex justify-end">
            <button
              onClick={onModalClose}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleGitAuthRemoval}
            >
              Remove
            </button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

const IntegrationTile = ({
  item,
  onClose,
  filterRemovedIntegration,
}: {
  item: IntegrationType;
  onClose: () => void;
  filterRemovedIntegration: (id) => void;
}) => {
  // const router = useRouter();
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();
  const [openSnackbar] = useSnackbar();

  // const handlePath = () => {
  //   switch (item.file.filetype) {
  //     case 'board':
  //       router.push(`/boards/${item.file.file._id}`);
  //       break;
  //     case 'docs':
  //       router.push(`/docs/${item.file.file._id}`);
  //       break;
  //   }
  //   onClose();
  // };

  const handleChannelRemoval = async () => {
    if (item.file?.file?._id) {
      const response = await API.delete(
        `/integrations?type=github&name=${item.repoName}&file=${item.file?.file?._id}`
      );
      if (response.data.success) {
        filterRemovedIntegration(item._id);
      }
    } else {
      openSnackbar({
        text: 'Cannot find file to remove integration',
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
    onModalClose();
  };

  return (
    <>
      <div className="flex border rounded-md py-12 px-20 mb-8">
        <span className="w-4/12 font-medium truncate pl-24 pr-6 items-center relative">
          <span className="mr-20 absolute top-2 left-0">
            {item.file.file?.icon?.id ? (
              <Emoji emoji={item.file.file?.icon?.id} size={20} />
            ) : (
              <BoardIcon wrapperClass="text-primary-300" />
            )}
          </span>
          {item.file?.file?.title || ''}
        </span>
        <span className="w-6/12 truncate pr-6 hover:underline">
          <a href={item.repo.html_url} target="_blank" rel="noreferrer">
            {item.repoName}
          </a>
        </span>
        <div className="w-2/12 flex justify-end align-middle">
          <button onClick={onOpen} className="text-gray-500 font-medium text-sm">
            <CloseIcon />
          </button>
        </div>
      </div>
      <Modal isOpen={isOpen} onClose={onModalClose}>
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">
            Are you sure you want to remove this Github integration?
          </p>
          <div className="flex justify-end">
            <button
              onClick={onModalClose}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleChannelRemoval}
            >
              Remove
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
